import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Mic2, Plus, Trash2, Check, Download, Volume2, Loader2, Square,
  ShieldCheck, ListMusic, Play, Search, Pencil, X,
} from 'lucide-react';
import { api } from '../../lib/api';
import { preloadAudios, cachedAudioUrl, countCachedAudios } from '../../lib/offline';
import { Field } from '../Shared';

/* Espace discours — projet distinct de la soirée invités.
   Onglet « Préparation » : rédaction et génération des réponses pré-générées.
   Onglet « Console » : déclenchement en direct, pensé pour le jour J
   (gros boutons, transcription à l'écran, lecture depuis le cache local). */

export default function AdminDiscours() {
  const [view, setView] = useState('prep');
  const [entries, setEntries] = useState([]);
  const [cachedCount, setCachedCount] = useState(0);
  const [err, setErr] = useState('');

  const refresh = useCallback(async () => {
    try {
      const data = await api.get('/api/admin/canned_responses?kind=discours');
      setEntries(data);
      setCachedCount(await countCachedAudios(data));
    } catch (e) {
      setErr(e.message);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <>
      <div className="tabs sub">
        <button className={`tab${view === 'prep' ? ' active' : ''}`} onClick={() => setView('prep')}>
          <ListMusic size={15} /> Préparation
        </button>
        <button className={`tab${view === 'console' ? ' active' : ''}`} onClick={() => setView('console')}>
          <Mic2 size={15} /> Console du jour J
        </button>
      </div>
      {err && <div className="test err">{err}</div>}
      {view === 'prep'
        ? <DiscoursPrep entries={entries} cachedCount={cachedCount} refresh={refresh} setErr={setErr} />
        : <DiscoursConsole entries={entries} cachedCount={cachedCount} refresh={refresh} setErr={setErr} />}
    </>
  );
}

/* ---------------- préparation ---------------- */
function DiscoursPrep({ entries, cachedCount, refresh, setErr }) {
  const [q, setQ] = useState('');
  const [ans, setAns] = useState('');
  const [genBusy, setGenBusy] = useState(null);
  const [genAllBusy, setGenAllBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editQ, setEditQ] = useState('');
  const [editAns, setEditAns] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  const add = async () => {
    if (!q.trim() || !ans.trim()) return;
    try {
      await api.post('/api/admin/canned_responses', {
        kind: 'discours', question: q.trim(), answer: ans.trim(), position: entries.length,
      });
      setQ(''); setAns('');
      refresh();
    } catch (e) { setErr(e.message); }
  };

  const rm = async (item) => {
    try { await api.del(`/api/admin/canned_responses/${item.id}`); refresh(); } catch (e) { setErr(e.message); }
  };

  const startEdit = (item) => {
    setEditId(item.id);
    setEditQ(item.question);
    setEditAns(item.answer);
  };

  // Si la réponse change, le serveur invalide l'audio : il repassera « à générer ».
  const saveEdit = async () => {
    setEditSaving(true);
    try {
      await api.patch(`/api/admin/canned_responses/${editId}`, { question: editQ.trim(), answer: editAns.trim() });
      setEditId(null);
      refresh();
    } catch (e) { setErr(e.message); }
    finally { setEditSaving(false); }
  };

  const genAudio = async (item) => {
    setGenBusy(item.id);
    try { await api.post(`/api/admin/canned_responses/${item.id}/generate_audio`); refresh(); }
    catch (e) { setErr(e.message); }
    finally { setGenBusy(null); }
  };

  // Génère tout ce qui manque : le jour J, chaque réponse doit avoir son audio.
  const genAll = async () => {
    setGenAllBusy(true);
    setErr('');
    try {
      for (const item of entries.filter((e) => !e.audio_available)) {
        await api.post(`/api/admin/canned_responses/${item.id}/generate_audio`);
      }
      refresh();
    } catch (e) { setErr(e.message); }
    finally { setGenAllBusy(false); }
  };

  const missing = entries.filter((e) => !e.audio_available).length;

  return (
    <div className="admin-grid">
      <div className="panel span2">
        <h3 className="panel-h"><ListMusic size={17} /> Réponses pré-générées du discours</h3>
        <p className="panel-p">
          Le CDC recommande le mode hybride : l'essentiel du discours sur audios pré-générés, deux ou trois
          questions réellement en direct. Ces réponses-ci sont la partie sûre — écrites à l'avance, vocalisées
          en voix clonée, jouées d'un clic depuis la console.
        </p>

        <div className="discours-stat">
          <span><b>{entries.length}</b> réponse{entries.length > 1 ? 's' : ''}</span>
          <span><b>{entries.length - missing}/{entries.length}</b> audio{entries.length > 1 ? 's' : ''} généré{entries.length > 1 ? 's' : ''}</span>
          <span><b>{cachedCount}</b> en cache sur cet appareil</span>
          {missing > 0 && (
            <button className="btn-ghost" onClick={genAll} disabled={genAllBusy}>
              {genAllBusy ? <><Loader2 size={15} className="spin" /> Génération…</> : <><Volume2 size={15} /> Générer les {missing} audios manquants</>}
            </button>
          )}
        </div>

        <div className="bank-add">
          <Field label="Question posée pendant le discours" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Olivier, un mot pour les mariés ?" />
          <Field label="Réponse d'Olivier" value={ans} onChange={(e) => setAns(e.target.value)} area placeholder="La réponse telle qu'elle sera prononcée…" />
          <button className="btn-primary" onClick={add} disabled={!q.trim() || !ans.trim()}><Plus size={15} /> Ajouter au discours</button>
        </div>

        <div className="bank-list">
          {entries.map((e) => (
            <div key={e.id} className="bank-item">
              {editId === e.id ? (
                <>
                  <Field label="Question posée pendant le discours" value={editQ} onChange={(ev) => setEditQ(ev.target.value)} />
                  <Field label="Réponse d'Olivier" value={editAns} onChange={(ev) => setEditAns(ev.target.value)} area />
                  {editAns.trim() !== e.answer && (
                    <div className="hint left">La réponse change : l'audio actuel sera invalidé, il faudra le regénérer.</div>
                  )}
                  <div className="bank-foot">
                    <button className="btn-primary" onClick={saveEdit} disabled={!editQ.trim() || !editAns.trim() || editSaving}>
                      {editSaving ? <Loader2 size={13} className="spin" /> : <Check size={13} />} Enregistrer
                    </button>
                    <button className="mini-btn" onClick={() => setEditId(null)} disabled={editSaving}><X size={13} /> annuler</button>
                  </div>
                </>
              ) : (
                <>
                  <div className="bank-q">{e.question}</div>
                  <div className="bank-a">{e.answer}</div>
                  <div className="bank-foot">
                    <span className={`cache-chip${e.audio_available ? ' ok' : ''}`}>
                      {e.audio_available ? <><Check size={12} /> audio prêt</> : <><Download size={12} /> audio à générer</>}
                    </span>
                    {!e.audio_available && (
                      <button className="mini-btn" onClick={() => genAudio(e)} disabled={genBusy !== null}>
                        {genBusy === e.id ? <Loader2 size={13} className="spin" /> : <Volume2 size={13} />} générer
                      </button>
                    )}
                    <button className="mini-btn" onClick={() => startEdit(e)}><Pencil size={13} /> modifier</button>
                    <button className="mini-btn danger" onClick={() => rm(e)}><Trash2 size={14} /></button>
                  </div>
                </>
              )}
            </div>
          ))}
          {entries.length === 0 && <div className="empty">Aucune réponse de discours pour l'instant.</div>}
        </div>
      </div>
    </div>
  );
}

/* ---------------- console du jour J ---------------- */
function DiscoursConsole({ entries, cachedCount, refresh, setErr }) {
  const [filter, setFilter] = useState('');
  const [playing, setPlaying] = useState(null);
  const [transcript, setTranscript] = useState(null);
  const [preload, setPreload] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => () => audioRef.current?.pause(), []);

  const stop = () => {
    audioRef.current?.pause();
    audioRef.current = null;
    setPlaying(null);
  };

  // Cliquer la tuile en cours de lecture l'arrête ; en cliquer une autre enchaîne.
  const trigger = async (item) => {
    if (playing === item.id) {
      stop();
      return;
    }
    stop();
    setTranscript(item);
    if (!item.audio_url) return;
    setPlaying(item.id);
    try {
      // Priorité au cache local : le jour J, le réseau n'est pas une hypothèse fiable.
      const url = (await cachedAudioUrl(item.audio_url)) || item.audio_url;
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => setPlaying(null);
      await audio.play();
    } catch (e) {
      setErr(e.message || 'Lecture impossible.');
      setPlaying(null);
    }
  };

  const startPreload = async () => {
    setPreload({ done: 0, total: 1 });
    try {
      const pack = await api.get('/api/admin/discours_pack');
      const res = await preloadAudios(pack.entries, (done, total) => setPreload({ done, total }));
      setPreload('done');
      refresh();
      if (res.audios < res.withAudio) setErr(`${res.withAudio - res.audios} audio(s) n'ont pas pu être mis en cache.`);
    } catch (e) {
      setErr(e.message);
      setPreload(null);
    }
  };

  const shown = entries.filter((e) => {
    if (!filter.trim()) return true;
    const f = filter.toLowerCase();
    return e.question.toLowerCase().includes(f) || e.answer.toLowerCase().includes(f);
  });
  const missing = entries.filter((e) => !e.audio_available).length;

  return (
    <div className="admin-grid">
      <div className="panel span2">
        <div className="console-head">
          <h3 className="panel-h"><Mic2 size={17} /> Console du discours</h3>
          <div className="console-actions">
            {playing !== null && (
              <button className="btn-ghost danger" onClick={stop}><Square size={15} /> Couper le son</button>
            )}
            {preload === null || preload === 'done' ? (
              <button className="btn-ghost" onClick={startPreload}>
                <Download size={15} /> {preload === 'done' ? 'Précharger à nouveau' : 'Précharger sur ce poste'}
              </button>
            ) : (
              <div className="preload-bar">
                <div className="preload-fill" style={{ width: `${Math.round((preload.done / Math.max(1, preload.total)) * 100)}%` }} />
                <span>{preload.done}/{preload.total}</span>
              </div>
            )}
          </div>
        </div>

        <div className={`console-status${missing === 0 && cachedCount >= entries.length && entries.length > 0 ? ' ready' : ''}`}>
          <ShieldCheck size={15} />
          {entries.length === 0
            ? "Aucune réponse préparée : passez par l'onglet Préparation."
            : missing > 0
              ? `${missing} réponse(s) sans audio — générez-les avant la soirée.`
              : cachedCount >= entries.length
                ? 'Poste prêt : tous les audios sont en cache local, la console fonctionne sans réseau.'
                : `${cachedCount}/${entries.length} audios en cache — préchargez ce poste avant le jour J.`}
        </div>

        {entries.length > 0 && (
          <label className="console-search">
            <Search size={15} />
            <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filtrer les réponses…" />
          </label>
        )}

        <div className="console-grid">
          {shown.map((e) => (
            <button
              key={e.id}
              className={`console-btn${playing === e.id ? ' playing' : ''}${e.audio_available ? '' : ' noaudio'}`}
              onClick={() => trigger(e)}
            >
              <span className="console-btn-q">{e.question}</span>
              <span className="console-btn-state">
                {playing === e.id
                  ? <><Square size={13} /> arrêter</>
                  : e.audio_available ? <><Play size={13} /> déclencher</> : 'audio manquant'}
              </span>
            </button>
          ))}
          {shown.length === 0 && entries.length > 0 && <div className="empty">Aucune réponse ne correspond au filtre.</div>}
        </div>

        {transcript && (
          <div className="console-transcript">
            <div className="console-transcript-q">{transcript.question}</div>
            <div className="console-transcript-a">{transcript.answer}</div>
          </div>
        )}
      </div>
    </div>
  );
}
